// Bound decoded cover textures and prioritize the selected record over neighbors.
export class CoverCache {
  constructor(load, dispose, { limit = 5, parallel = 2 } = {}) {
    this.load = load;
    this.dispose = dispose;
    this.limit = limit;
    this.parallel = parallel;
    this.textures = new Map();
    this.jobs = new Map();
    this.queue = [];
    this.wanted = new Set();
    this.active = "";
    this.running = 0;
    this.failedAt = new Map();
  }
  setWanted(urls) {
    const unique = [...new Set(urls.filter(Boolean))];
    this.wanted = new Set(unique);
    this.active = unique[0] || "";
    this.queue = this.queue.filter((job) => {
      if (this.wanted.has(job.url)) return true;
      this.jobs.delete(job.url);
      job.resolve(null);
      return false;
    });
    for (const [index, url] of unique.entries())
      void this.request(url, index === 0 ? 10 : 0).catch(() => {});
    this.trim();
  }
  request(url, priority = 0) {
    if (!url) return Promise.resolve(null);
    const cached = this.textures.get(url);
    if (cached) {
      this.textures.delete(url);
      this.textures.set(url, cached);
      return Promise.resolve(cached);
    }
    const pending = this.jobs.get(url);
    if (pending) {
      pending.priority = Math.max(pending.priority, priority);
      return pending.promise;
    }
    if (Date.now() - (this.failedAt.get(url) || 0) < 8000)
      return Promise.resolve(null);
    const job = { url, priority };
    job.promise = new Promise((resolve, reject) => {
      job.resolve = resolve;
      job.reject = reject;
    });
    this.jobs.set(url, job);
    this.queue.push(job);
    this.drain();
    return job.promise;
  }
  drain() {
    while (this.running < this.parallel && this.queue.length) {
      this.queue.sort((a, b) => b.priority - a.priority);
      const job = this.queue.shift();
      this.running++;
      Promise.resolve().then(() => this.load(job.url)).then((texture) => {
        if (texture && this.wanted.has(job.url)) {
          this.textures.set(job.url, texture);
          this.trim();
          job.resolve(texture);
        } else {
          if (texture) this.dispose(texture);
          job.resolve(null);
        }
      }).catch((error) => {
        this.failedAt.set(job.url, Date.now());
        job.reject(error);
      }).finally(() => {
        this.jobs.delete(job.url);
        this.running--;
        this.drain();
      });
    }
  }
  trim() {
    while (this.textures.size > this.limit) {
      const oldest = [...this.textures.keys()].find((url) => url !== this.active);
      if (!oldest) break;
      this.dispose(this.textures.get(oldest));
      this.textures.delete(oldest);
    }
  }
}
